import { defineStore } from 'pinia'
import { queryGoodsOption } from '@/api/goodsSetting'
import { queryGoodsOption as queryGoodsInfoOption } from '@/api/goodsInfo'

export interface AppDimensionOption {
  dbtId: string
  appName: string
  label: string
  value: string
  hasFavorite?: boolean
  extData: Record<string, unknown>
  filePath: string
  [key: string]: unknown
}

type AppDimensionResponse = {
  code?: string | number
  data?: unknown[]
}

const normalizeOptions = (data?: unknown[]): AppDimensionOption[] => {
  return (data || []).map((item) => {
    const source = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
    const extData =
      source.extData && typeof source.extData === 'object' ? (source.extData as Record<string, unknown>) : {}
    const dbtId = String(source.dbtId || '')
    const appName = String(source.appName || '')

    return {
      ...source,
      ...extData,
      dbtId,
      appName,
      label: appName,
      value: dbtId,
      extData,
      filePath: typeof extData.filePath === 'string' ? extData.filePath : '-',
    } as AppDimensionOption
  })
}

let goodsSettingPromise: Promise<void> | null = null
let goodsJudgePromise: Promise<void> | null = null
let goodsInfoPromise: Promise<void> | null = null
let goodsManagePromise: Promise<void> | null = null

export const useAppDimensionStore = defineStore('appDimension', {
  state: () => ({
    goodsSettingProducts: [] as AppDimensionOption[],
    goodsJudgeProducts: [] as AppDimensionOption[],
    goodsInfoProducts: [] as AppDimensionOption[],
    goodsSettingLoaded: false,
    goodsJudgeLoaded: false,
    goodsInfoLoaded: false,
    goodsManageLoading: false,
  }),

  actions: {
    async loadGoodsSettingDimensions(force = false) {
      if (this.goodsSettingLoaded && !force) return
      if (goodsSettingPromise) return goodsSettingPromise

      goodsSettingPromise = (async () => {
        try {
          const response = (await queryGoodsOption()) as AppDimensionResponse
          if (String(response?.code) === '0') {
            this.goodsSettingProducts = normalizeOptions(response.data)
          }
        } catch (error) {
          console.error('Failed to load goods setting dimensions:', error)
        } finally {
          this.goodsSettingLoaded = true
        }
      })().finally(() => {
        goodsSettingPromise = null
      })

      return goodsSettingPromise
    },

    async loadGoodsJudgeDimensions(force = false) {
      if (this.goodsJudgeLoaded && !force) return
      if (goodsJudgePromise) return goodsJudgePromise

      goodsJudgePromise = (async () => {
        try {
          const response = (await queryGoodsOption({ showProducts: true })) as AppDimensionResponse
          if (String(response?.code) === '0') {
            this.goodsJudgeProducts = normalizeOptions(response.data)
          }
        } catch (error) {
          console.error('Failed to load goods judge dimensions:', error)
        } finally {
          this.goodsJudgeLoaded = true
        }
      })().finally(() => {
        goodsJudgePromise = null
      })

      return goodsJudgePromise
    },

    async loadGoodsInfoDimensions(force = false) {
      if (this.goodsInfoLoaded && !force) return
      if (goodsInfoPromise) return goodsInfoPromise

      goodsInfoPromise = (async () => {
        try {
          const response = (await queryGoodsInfoOption({ accountType: 2 })) as AppDimensionResponse
          if (String(response?.code) === '0') {
            this.goodsInfoProducts = normalizeOptions(response.data)
          }
        } catch (error) {
          console.error('Failed to load goods info dimensions:', error)
        } finally {
          this.goodsInfoLoaded = true
        }
      })().finally(() => {
        goodsInfoPromise = null
      })

      return goodsInfoPromise
    },

    async loadGoodsManageDimensions() {
      if (this.goodsSettingLoaded && this.goodsJudgeLoaded && this.goodsInfoLoaded) return
      if (goodsManagePromise) return goodsManagePromise

      this.goodsManageLoading = true
      goodsManagePromise = Promise.all([
        this.loadGoodsSettingDimensions(),
        this.loadGoodsJudgeDimensions(),
        this.loadGoodsInfoDimensions(),
      ])
        .then(() => undefined)
        .finally(() => {
          this.goodsManageLoading = false
          goodsManagePromise = null
        })

      return goodsManagePromise
    },

    resetGoodsManageDimensions() {
      this.goodsSettingProducts = []
      this.goodsJudgeProducts = []
      this.goodsInfoProducts = []
      this.goodsSettingLoaded = false
      this.goodsJudgeLoaded = false
      this.goodsInfoLoaded = false
      this.goodsManageLoading = false
      goodsSettingPromise = null
      goodsJudgePromise = null
      goodsInfoPromise = null
      goodsManagePromise = null
    },
  },
})
